/**
 * Streaming-style CSV download helper.
 *
 * The standard `get/post/patch` helpers in `api.ts` go through axios with
 * `Content-Type: application/json` and parse the body. CSV exports need a
 * raw blob + a server-side `Content-Disposition: attachment` to drive the
 * browser-native download. We use `fetch` here directly (instead of axios)
 * so we don't carry a transform-response pipeline we'd have to bypass.
 *
 * Throws on non-2xx; callers wire the error message into a toast.
 */
import { useAuthStore } from './auth';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4100';
const API_PREFIX = import.meta.env.VITE_API_PREFIX ?? '/v1';

export interface DownloadResult {
  /** Approximate row count derived from the CSV (subtracts the header row). */
  rowCount: number;
  /** Total byte size streamed to the client. */
  byteSize: number;
}

interface ApiErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
}

/**
 * Download an authenticated CSV endpoint as a file. The `url` is appended
 * to the API base URL + prefix; pass the path-only segment beginning with
 * a slash (e.g. `/superadmin/societies/export.csv?country=IN`).
 *
 * Returns the row count + byte size so callers can render a toast like
 * "Downloaded 42 rows".
 */
export async function downloadAsCsv(
  url: string,
  filename: string,
  jwt?: string | null
): Promise<DownloadResult> {
  const token = jwt ?? useAuthStore.getState().accessToken;
  const fullUrl = `${API_URL}${API_PREFIX}${url}`;
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(fullUrl, { headers });
  if (!res.ok) {
    let parsed: ApiErrorBody = {};
    try {
      parsed = (await res.json()) as ApiErrorBody;
    } catch {
      // body wasn't JSON; fall through to the generic message
    }
    throw new Error(parsed.error?.message ?? `Export failed: ${res.status}`);
  }

  const blob = await res.blob();
  const text = await blob.text();
  // Subtract one for the header row; cap at 0 for empty CSVs.
  const lineCount = text.length === 0 ? 0 : text.split(/\r?\n/).filter((l) => l.length > 0).length;
  const rowCount = Math.max(0, lineCount - 1);

  // Trigger browser download via an anchor + object URL.
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);

  return { rowCount, byteSize: blob.size };
}

/** Build the canonical filename: `hooddesk-{kind}-{YYYY-MM-DD}.csv`. */
export function csvFilename(kind: 'tenants' | 'subscriptions' | 'leads'): string {
  const d = new Date();
  const iso =
    d.getUTCFullYear().toString() +
    '-' +
    String(d.getUTCMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getUTCDate()).padStart(2, '0');
  return `hooddesk-${kind}-${iso}.csv`;
}
